import { useFetch2 } from "../../../util";

export type PipelineItem = {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  createdById: number;
  createdByName: string;
  updatedById: number;
  updatedByName: string;
  workspaceId: number | null;
};

export type GetStagesByPipelineResponse = {
  pipelineId: number;
  pipelineName: string;
  stages: StageItem[];
};

export type StageItem = {
  id: number;
  pipelineId: number;
  name: string;
  position: number;
  color: string;
  createdAt: string;
  updatedAt: string;
  createdById: number;
  createdByName: string;
  updatedById: number;
  updatedByName: string;
  cards: CardItem[];
};

export type CardItem = {
  id: number;
  stageId: number;
  contactName: string;
  source: string;
  botId: number | null;
  botName: string | null;
  createdAt: string;
  updatedAt: string;
  userId: number | null;
  userName: string | null;
  labelIds: number[];
};

export type GetCardByIdResponse = {
  id: number;
  pipelineId: number | null;
  stageId: number | null;
  labelIds: number[];
  lossReasonId: number | null;
  contactId: number;
  contactName: string;
  contactPhone: string;
  contactEmail: string | null;
  userId: number | null;
  userName: string | null;
  botId: number | null;
  botName: string | null;
  source: string;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdByName: string;
  updatedByName: string;
};

export type UpdateCardFormValues = {
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  contactWorkspaceId: number | null;
  stageId: number | null;
  labelIds?: number[] | null;
  lossReasonId?: number | null;
  userId: number | null;
  botId: number | null;
  source: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  updatedByName: string;
};

export type StageFormValues = {
  name: string;
  color: string;
};

export type SaveStagePayload = {
  id?: number;
  name: string;
  color: string;
  position: number;
};

export type LabelItem = {
  id: number;
  name: string;
  color: string;
};

export type SaveLabelPayload = {
  id?: number;
  name: string;
  color: string;
};

export type SaveCardPayload = {
  pipelineId: number;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  contactWorkspaceId: number | null;
  stageId: number | null;
  labelIds?: number[] | null;
  lossReasonId?: number | null;
  userId: number | null;
  botId: number | null;
  source: string;
};

export type EditCardPayload = {
  id: number;
  pipelineId: number;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  stageId: number | null;
  labelIds?: number[] | null;
  lossReasonId?: number | null;
  userId: number | null;
  botId: number | null;
  source: string;
};

export type FilterPipelineCardsResponse = {
  pipelineId: number;
  stages: {
    stageId: number;
    cards: CardItem[];
  }[];
};

export type CardNoteItem = {
  id: number;
  cardId: number;
  note: string;
  createdAt: string;
  updatedAt: string | null;
  createdByName: string;
  updatedByName: string | null;
};

export type CreateCardNotePayload = {
  cardId: number;
  note: string;
};

export type EditCardNotePayload = {
  id: number;
  note: string;
};

export type CardLossReasonItem = {
  id: number;
  cardId?: number | null;
  reason: string;
  createdAt: string;
  updatedAt: string | null;
  createdByName: string;
  updatedByName: string | null;
};

export type CreateCardLossReasonPayload = {
  companyId: number;
  reason: string;
};

export type EditCardLossReasonPayload = {
  id: number;
  reason: string;
};

export default function usePipelinesApi() {
  const fetch = useFetch2();

  const getPipelineByWorkspacesIds = (
    companyId: number,
    workspaceIds: number[]
  ) => {
    return fetch(
      `/api/pipelines/pipeline`,
      { method: "POST" },
      { companyId, workspaceIds }
    );
  };

  const getPipelinesByCompanyId = (
    companyId: number,
    workspaceIds: number[]
  ) => {
    return fetch(
      `/api/pipelines/list`,
      { method: "POST" },
      { companyId, workspaceIds }
    );
  };

  const createPipeline = (
    companyId: number,
    name: string,
    workspaceId: number
  ) => {
    return fetch(
      `/api/pipelines/create/${companyId}`,
      { method: "POST" },
      { name, workspaceId }
    );
  };

  const editPipeline = (id: number, name: string, workspaceId: number) => {
    return fetch(
      `/api/pipelines`,
      { method: "PUT" },
      { id, name, workspaceId }
    );
  };

  const getStagesByPipeline = (
    pipelineId: number,
    sortField?: string,
    sortOrder?: number,
    userIds?: number[],
    labelIds?: number[],
    source?: string
  ) => {
    return fetch(
      `/api/pipeline-stages/list`,
      { method: "POST" },
      { pipelineId, sortField, sortOrder, userIds, labelIds, source }
    );
  };

  const filterCardsByPipeline = (
    pipelineId: number,
    search: string,
    sortField?: string,
    sortOrder?: number,
    userIds?: number[],
    labelIds?: number[],
    source?: string
  ) => {
    return fetch(
      `/api/pipeline-stages/filter-cards`,
      { method: "POST" },
      { pipelineId, search, sortField, sortOrder, userIds, labelIds, source }
    );
  };

  const changeCardStage = (cardId: number, stageId: number) => {
    return fetch(
      `/api/pipeline-cards/change-stage`,
      { method: "PATCH" },
      { id: cardId, stageId }
    );
  };

  const saveStages = (pipelineId: number, payload: SaveStagePayload[]) => {
    return fetch(
      `/api/pipeline-stages/${pipelineId}`,
      { method: "PUT" },
      payload
    );
  };

  const deleteStage = (id: number) => {
    return fetch(`/api/pipeline-stages/${id}`, { method: "DELETE" });
  };

  const listCardsByStage = (
    stageId: number,
    page: number,
    search?: string,
    sortField?: string,
    sortOrder?: number,
    userIds?: number[],
    labelIds?: number[],
    source?: string
  ) => {
    return fetch(
      `/api/pipeline-cards/list-by-stage`,
      { method: "POST" },
      { stageId, page, search, sortField, sortOrder, userIds, labelIds, source }
    );
  };

  const getCardsById = (cardId: number) => {
    return fetch(`/api/pipeline-cards/card/${cardId}`, { method: "GET" });
  };

  const createCard = (payload: SaveCardPayload) => {
    return fetch(`/api/pipeline-cards`, { method: "POST" }, payload);
  };

  const editCard = (payload: EditCardPayload) => {
    return fetch(`/api/pipeline-cards`, { method: "PATCH" }, payload);
  };

  const getCardNotes = (cardId: number) => {
    return fetch(`/api/pipeline-card-note/${cardId}`, { method: "GET" });
  };

  const createCardNote = (payload: CreateCardNotePayload) => {
    return fetch(`/api/pipeline-card-note`, { method: "POST" }, payload);
  };

  const editCardNote = (payload: EditCardNotePayload) => {
    return fetch(`/api/pipeline-card-note`, { method: "PATCH" }, payload);
  };

  const deleteCardNote = (id: number) => {
    return fetch(`/api/pipeline-card-note/${id}`, { method: "DELETE" });
  };

  const getCardLossReasons = (companyId: number) => {
    return fetch(`/api/pipeline-card-loss-reason/company/${companyId}`, {
      method: "GET"
    });
  };

  const createCardLossReason = (payload: CreateCardLossReasonPayload) => {
    return fetch(`/api/pipeline-card-loss-reason`, { method: "POST" }, payload);
  };

  const editCardLossReason = (payload: EditCardLossReasonPayload) => {
    return fetch(`/api/pipeline-card-loss-reason`, { method: "PATCH" }, payload);
  };

  const deleteCardLossReason = (id: number) => {
    return fetch(`/api/pipeline-card-loss-reason/${id}`, { method: "DELETE" });
  };

  const getAllLabels = (companyId: number) => {
    return fetch(`/api/pipeline-label/${companyId}`, { method: "GET" });
  };

  const saveLabels = (companyId: number, payload: SaveLabelPayload[]) => {
    return fetch(
      `/api/pipeline-label/${companyId}`,
      { method: "POST" },
      payload
    );
  };

  const deleteLabel = (companyId: number, labelId: number) => {
    return fetch(`/api/pipeline-label/${companyId}/${labelId}`, {
      method: "DELETE"
    });
  };

  return {
    getPipelineByWorkspacesIds,
    getPipelinesByCompanyId,
    createPipeline,
    editPipeline,
    getStagesByPipeline,
    filterCardsByPipeline,
    changeCardStage,
    saveStages,
    deleteStage,
    listCardsByStage,
    getCardsById,
    createCard,
    editCard,
    getCardNotes,
    createCardNote,
    editCardNote,
    deleteCardNote,
    getCardLossReasons,
    createCardLossReason,
    editCardLossReason,
    deleteCardLossReason,
    getAllLabels,
    saveLabels,
    deleteLabel
  };
}
